/**
 * 滑块游戏核心逻辑
 * 数据结构：滑块表 - K*6数组，每行保存滑块ID、头部位置(x,y)、朝向(dx,dy)、存活状态
 */
import { gameConfig } from '../gameConfig';

export class SliderGame {
  constructor(m, n, k) {
    this.m = m; // 行数
    this.n = n; // 列数
    this.k = k; // 滑块数量
    this.sliders = []; // 滑块数组 [id, x, y, dx, dy, alive]
    this.grid = Array(m).fill().map(() => Array(n).fill(0)); // 网格状态
    this.steps = 0; // 步数
    this.gameOver = false;
    
    this.initializeGame();
  }

  /**
   * 初始化游戏
   */
  initializeGame() {
    this.sliders = [];
    this.grid = Array(this.m).fill().map(() => Array(this.n).fill(0));
    this.steps = 0;
    this.gameOver = false;

    // 随机生成滑块
    for (let i = 1; i <= this.k; i++) {
      this.generateRandomSlider(i);
    }
  }

  /**
   * 随机生成一个滑块
   */
  generateRandomSlider(id) {
    let attempts = 0;
    const maxAttempts = 100;

    while (attempts < maxAttempts) {
      // 随机选择头部位置
      const headX = Math.floor(Math.random() * this.m);
      const headY = Math.floor(Math.random() * this.n);
      
      // 随机选择朝向（上下左右）
      const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      const [dx, dy] = directions[Math.floor(Math.random() * directions.length)];
      
      // 规则1：新滑块的任何部分不能与既有滑块的任何部分重合
      const cells = [];
      let valid = true;
      
      for (let i = 0; i < gameConfig.sliderLength; i++) {
        const x = headX - dx * (gameConfig.sliderLength - 1 - i);
        const y = headY - dy * (gameConfig.sliderLength - 1 - i);
        
        if (!this.isValidPosition(x, y) || this.grid[x][y] !== 0) {
          valid = false;
          break;
        }
        cells.push([x, y]);
      }
      
      if (!valid) {
        attempts++;
        continue;
      }
      
      // 规则2：新滑块的头部的前方直到网格边界，不能存在某个方向与它相反的滑块
      const oppositeDx = -dx;
      const oppositeDy = -dy;
      let hasOppositeSlider = false;
      
      for (let checkX = headX + dx, checkY = headY + dy; 
           this.isValidPosition(checkX, checkY); 
           checkX += dx, checkY += dy) {
        const oppositeSlider = this.sliders.some(slider => {
          const [, sHeadX, sHeadY, sDx, sDy, alive] = slider;
          return alive && 
                 sHeadX === checkX && // 理论上来说可以优化算法但是没必要了
                 sHeadY === checkY && 
                 sDx === oppositeDx && 
                 sDy === oppositeDy;
        });
        if (oppositeSlider) {
          hasOppositeSlider = true;
          break;
        }
      }
      
      if (hasOppositeSlider) {
        attempts++;
        continue;
      }
      
      // 检查头部是否直接对着边界（应该避免）
      const nextHeadX = headX + dx;
      const nextHeadY = headY + dy;
      if (!this.isValidPosition(nextHeadX, nextHeadY)) {
        attempts++;
        continue;
      }
      
      // 创建滑块
      const slider = [id, headX, headY, dx, dy, 1];
      this.sliders.push(slider);
      
      // 更新网格
      cells.forEach(([x, y]) => {
        this.grid[x][y] = id;
      });
      
      return;
    }
    
    // 如果无法放置，减少滑块数量
    this.k--;
  }

  /**
   * 检查位置是否有效
   */
  isValidPosition(x, y) {
    return x >= 0 && x < this.m && y >= 0 && y < this.n;
  }

  /**
   * 获取滑块占据的所有格子
   */
  getSliderCells(slider) {
    const [, headX, headY, dx, dy] = slider;
    const cells = [];
    
    // 从尾部开始，沿着朝向方向延伸
    for (let i = 0; i < gameConfig.sliderLength; i++) {
      const x = headX - dx * (gameConfig.sliderLength - 1 - i);
      const y = headY - dy * (gameConfig.sliderLength - 1 - i);
      cells.push([x, y]);
    }
    
    return cells;
  }

  /**
   * 查询滑块头部到前方最近障碍的距离
   */
  getDistanceToObstacle(slider) {
    const [id, headX, headY, dx, dy] = slider;
    let distance = 0;
    let currentX = headX + dx;
    let currentY = headY + dy;

    while (this.isValidPosition(currentX, currentY)) {
      if (this.grid[currentX][currentY] !== 0 && this.grid[currentX][currentY] !== id) {
        break;
      }
      distance++;
      currentX += dx;
      currentY += dy;
    }

    return distance;
  }

  /**
   * 查询滑块头部到前方边界的距离
   */
  getDistanceToBoundary(slider) {
    const [, headX, headY, dx, dy] = slider;
    let distance = 0;
    let currentX = headX + dx;
    let currentY = headY + dy;

    while (this.isValidPosition(currentX, currentY)) {
      distance++;
      currentX += dx;
      currentY += dy;
    }

    return distance;
  }

  /**
   * 检查滑块是否可以移动
   */
  canMoveSlider(slider) {
    const [id, headX, headY, dx, dy, alive] = slider;
    if (!alive) return false;

    const newHeadX = headX + dx;
    const newHeadY = headY + dy;

    // 检查新头部位置是否有效
    if (!this.isValidPosition(newHeadX, newHeadY)) return false;
    
    // 检查新头部位置是否被其他滑块占据
    const gridValue = this.grid[newHeadX][newHeadY];
    if (gridValue !== 0 && gridValue !== id) return false;

    return true;
  }

  /**
   * 移动滑块
   */
  moveSlider(sliderId) {
    if (this.gameOver) return { success: false, eliminatedSliders: [] };

    const slider = this.sliders.find(s => s[0] === sliderId);
    if (!slider || !slider[5]) return { success: false, eliminatedSliders: [] };

    const [id, headX, headY, dx, dy] = slider;

    if (!this.canMoveSlider(slider)) return { success: false, eliminatedSliders: [] };

    // 清除当前位置
    const cells = this.getSliderCells(slider);
    cells.forEach(([x, y]) => {
      this.grid[x][y] = 0;
    });

    // 更新滑块位置
    const newHeadX = headX + dx;
    const newHeadY = headY + dy;
    slider[1] = newHeadX;
    slider[2] = newHeadY;

    const eliminatedSliders = [];

    // 检查是否触碰边界
    if (!this.isValidPosition(newHeadX + dx, newHeadY + dy)) {
      // 滑块到达边界，立即标记死亡
      slider[5] = 0;
      eliminatedSliders.push(id);
      
      // 不更新网格，直接清除（因为滑块已经死亡）
    } else {
      // 更新网格
      const newCells = this.getSliderCells(slider);
      newCells.forEach(([x, y]) => {
        this.grid[x][y] = id;
      });
    }

    this.steps++;
    this.checkGameOver();
    return { success: true, eliminatedSliders };
  }

  /**
   * 检查游戏是否结束
   */
  checkGameOver() {
    const aliveSliders = this.sliders.filter(s => s[5] === 1);
    if (aliveSliders.length === 0) {
      this.gameOver = true;
      return;
    }

    // 检查是否还有可移动的滑块
    const hasMovableSliders = aliveSliders.some(slider => this.canMoveSlider(slider));
    if (!hasMovableSliders) {
      this.gameOver = true;
    }
  }

  /**
   * 获取游戏状态
   */
  getGameState() {
    return {
      grid: this.grid,
      sliders: this.sliders,
      steps: this.steps,
      remainingSliders: this.sliders.filter(s => s[5] === 1).length,
      totalSliders: this.k,
      gameOver: this.gameOver
    };
  }

  /**
   * 重置游戏
   */
  reset() {
    this.initializeGame();
  }

  /**
   * 获取可移动的滑块列表（用于程序控制）
   */
  getMovableSliders() {
    return this.sliders.filter(s => s[5] === 1 && this.canMoveSlider(s));
  }

  /**
   * 执行一步移动（用于程序控制）
   */
  makeMove(sliderId) {
    return this.moveSlider(sliderId);
  }
}